import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import Section, { Reveal, SectionHeading } from './Section'

/** One bento cell: a small visual on top, a bold-lead title and a line of copy underneath. */
function Card({ lead, rest, body, children, className = '', delay = 0 }: { lead: string; rest: string; body: string; children: ReactNode; className?: string; delay?: number }) {
  return (
    <div className={`bg-[#030712] ${className}`}>
      <Reveal delay={delay} className="flex h-full flex-col p-6 md:p-8">
        <div className="flex min-h-[176px] flex-1 items-center justify-center rounded-lg border border-white/[0.06] bg-white/[0.015] p-5">{children}</div>
        <h3 className="mt-6 font-display text-lg leading-snug text-slate-500">
          <span className="font-semibold text-slate-100">{lead}</span> {rest}
        </h3>
        <p className="mt-2 font-display text-[15px] leading-relaxed text-slate-400">{body}</p>
      </Reveal>
    </div>
  )
}

const chip = 'rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-wider'

function RecognitionVisual() {
  return (
    <div className="flex w-full max-w-lg flex-col items-center gap-5 sm:flex-row">
      <svg viewBox="0 0 160 104" className="w-full max-w-[220px] shrink-0" fill="none" aria-hidden="true">
        <rect x="4" y="4" width="152" height="96" stroke="#94a3b8" />
        <rect x="18" y="18" width="50" height="24" stroke="#22d3ee" fill="rgba(6,182,212,0.08)" />
        <rect x="92" y="18" width="50" height="24" stroke="#22d3ee" fill="rgba(6,182,212,0.08)" />
        {[34, 66, 98, 130].map(cx => <circle key={cx} cx={cx} cy="72" r="5" stroke="#f472b6" fill="rgba(244,114,182,0.08)" />)}
        <rect x="1" y="1" width="158" height="102" stroke="#fbbf24" strokeDasharray="3 3" strokeOpacity="0.6" />
      </svg>
      <ul className="flex flex-wrap gap-2 sm:flex-col">
        <li className={`${chip} border-cyan-500/30 text-cyan-300`}>2 × Pocket · 8 deep</li>
        <li className={`${chip} border-pink-400/30 text-pink-300`}>4 × Hole · Ø6.8 thru</li>
        <li className={`${chip} border-amber-400/30 text-amber-300`}>1 × Profile · 120×80</li>
      </ul>
    </div>
  )
}

const TOOLS = [['T01', 'Face mill', 'Ø50'], ['T02', 'Endmill', 'Ø12'], ['T03', 'Endmill', 'Ø6'], ['T04', 'Drill', 'Ø6.8']]

function ToolsVisual() {
  return (
    <table className="w-full max-w-xs font-mono text-xs">
      <tbody>
        {TOOLS.map(([n, type, d]) => (
          <tr key={n} className="border-b border-white/[0.06] last:border-0">
            <td className="py-2 pr-3 text-cyan-400">{n}</td>
            <td className="py-2 pr-3 text-slate-300">{type}</td>
            <td className="py-2 text-right tabular-nums text-slate-500">{d}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const CHECKS = [['Geometry match', '94%'], ['Tool reach', 'OK'], ['Collision check', 'OK']]

function VerifyVisual() {
  return (
    <ul className="w-full max-w-xs space-y-2.5">
      {CHECKS.map(([name, value]) => (
        <li key={name} className="flex items-center gap-3 rounded-md border border-emerald-400/15 bg-emerald-400/[0.04] px-3 py-2 font-mono text-xs">
          <Check className="h-3.5 w-3.5 text-emerald-400" />
          <span className="text-slate-300">{name}</span>
          <span className="ml-auto tabular-nums text-emerald-400">{value}</span>
        </li>
      ))}
    </ul>
  )
}

const MACHINE = [['Travel', '760 × 400 × 500'], ['Spindle', '8,000 rpm'], ['Tool changer', '20 pockets']]

function MachineVisual() {
  return (
    <dl className="w-full max-w-xs font-mono text-xs">
      <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />VMC · Bay 2
      </div>
      {MACHINE.map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-white/[0.06] py-2 last:border-0">
          <dt className="text-slate-500">{k}</dt>
          <dd className="tabular-nums text-slate-200">{v}</dd>
        </div>
      ))}
    </dl>
  )
}

function ChatVisual() {
  return (
    <div className="w-full max-w-xs space-y-2.5 font-display text-sm">
      <p className="ml-auto w-fit max-w-[90%] rounded-lg rounded-br-sm bg-cyan-500/15 px-3 py-2 text-cyan-100">Use a 10 mm endmill for the pockets.</p>
      <p className="w-fit max-w-[90%] rounded-lg rounded-bl-sm border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-slate-300">
        Swapped T02 to Ø10. Re-planned 3 operations.
      </p>
    </div>
  )
}

const OPS = [['Face', 'T01'], ['Rough', 'T02'], ['Finish', 'T03'], ['Drill', 'T04'], ['Contour', 'T02']]

function OperationsVisual() {
  return (
    <ol className="flex w-full max-w-3xl flex-wrap items-center justify-center gap-2 sm:gap-0">
      {OPS.map(([op, tool], i) => (
        <li key={op} className="flex items-center">
          <span className="flex flex-col items-center rounded-lg border border-white/[0.08] bg-[#030712] px-4 py-2.5">
            <span className="font-display text-sm font-semibold text-slate-100">{op}</span>
            <span className="font-mono text-[10px] text-cyan-400">{tool}</span>
          </span>
          {i < OPS.length - 1 && <span aria-hidden="true" className="mx-2 hidden h-px w-5 bg-cyan-500/40 sm:block md:w-10" />}
        </li>
      ))}
    </ol>
  )
}

/** Feature bento: 1px gutters drawn by the grid background, cells on the page colour. */
export default function FeatureBento() {
  return (
    <Section id="features" labelledBy="features-title">
      <div className="px-5 pt-20 md:px-12 md:pt-28">
        <SectionHeading id="features-title" label="Features" lead="Everything between the drawing" rest="and the spindle." />
      </div>
      <div className="mt-14 grid gap-px border-t border-white/[0.07] bg-white/[0.07] md:grid-cols-3">
        <Card className="md:col-span-2" lead="Feature recognition" rest="that reads the drawing like a machinist."
          body="Pockets, holes and profiles found in the geometry, with dimensions and notes read alongside.">
          <RecognitionVisual />
        </Card>
        <Card delay={0.08} lead="Tool selection" rest="for every feature." body="Tools, order and feeds and speeds chosen for each operation.">
          <ToolsVisual />
        </Card>
        <Card lead="Verification" rest="before it reaches the machine." body="Every program is checked against your drawing, tool reach and collisions.">
          <VerifyVisual />
        </Card>
        <Card delay={0.08} lead="Machine profiles" rest="for your shop." body="Work envelope, spindle and tool changer, so programs fit the machine you have.">
          <MachineVisual />
        </Card>
        <Card delay={0.16} lead="Plain-language edits." rest="Say what to change." body="Describe the job the way you would to a colleague. GCodr updates the plan.">
          <ChatVisual />
        </Card>
        <Card className="md:col-span-3" lead="Multi-operation jobs," rest="one program."
          body="Facing, roughing, finishing, drilling and contouring, each with its own tool and work offsets.">
          <OperationsVisual />
        </Card>
      </div>
    </Section>
  )
}
